// @flow

import * as React from 'react';
import classnames from '@ta/common.classnames';
import throttle from '@ta/common.throttle';
import { makePoly, insidePoly } from '@ta/common.polygon';
import { useWindow } from '@ta/events.window';
import { useWindowScroll } from '@ta/events.window-scroll';
import ParentScroll from '@ta/events.parent-scroll';
import AttachedArrowOverlay from '@ta/overlays.attached-arrow-overlay';

import styles from './Tooltip.css';

type Side = 'top' | 'bottom' | 'right' | 'left' | 'windowX';
type Position = 'above' | 'below' | 'leftOf' | 'rightOf';
type MousePosition = { mouseX: number, mouseY: number };
type PixelCount = number;
type EventListenerAction = 'add' | 'remove';

type Props = {|
  /** The text of the tooltip */
  message: React.Node,

  /** If specified (One of above/below/leftOf/rightOf is required) positions the tooltip above the selected element */
  above?: boolean,

  /** If specified (One of above/below/leftOf/rightOf is required) positions the tooltip below the selected element */
  below?: boolean,

  /** If specified (One of above/below/leftOf/rightOf is required) positions the tooltip to the left of the element */
  leftOf?: boolean,

  /** If specified (One of above/below/leftOf/rightOf is required) positions the tooltip to the right of the element */
  rightOf?: boolean,

  /** If specified, aligns the given edges of the selected element and the tooltip */
  alignEdge?: Side,

  /** A pixel shift in the x dimension */
  shiftX?: PixelCount,

  /** A pixel shift in the y dimension */
  shiftY?: PixelCount,

  /** A pixel shift in the arrow dimension, for the arrow */
  shiftArrow?: PixelCount,

  /** If true, shows the tooltip with a dark theme */
  inverted?: boolean,

  /** allow the caller to set the max-width of the tooltip, the default max-width is 300 */
  maxWidth?: PixelCount,

  /** Render prop: The JSX the tooltip is for. Will be passed a ref consumer. */
  children: ((?HTMLElement) => void) => React.Node,

  /** callback when this tooltip is shown */
  onShow?: () => void,

  /** for touch events, option to toggle tooltip on touch instead of hide on touchEnd */
  toggleOnTouch?: boolean,

  /** should tooltip hide when parent is scrolled */
  hideOnParentScroll?: boolean,

  /** Whether the container should render with opinionated padding */
  fullBleed?: boolean,
|};

/** function return position of tooltip in dependence with container  */
export const getPosition = (props: any): Position => {
  if (props.above) {
    return 'above';
  }
  if (props.below) {
    return 'below';
  }
  if (props.leftOf) {
    return 'leftOf';
  }
  return 'rightOf';
};

/**
 * Tooltips are hover-shown overlays to display informational text related to
 * some element on the screen. The Tooltip component lets you attach to a
 * DOM element via a selector, and display the informational text as desired.
 *
 * For more information, see: https://docs.dev.tripadvisor.com/display/STYLE/Tooltips
 */
const Tooltip = (props: Props) => {
  const {
    onShow,
    toggleOnTouch,
    hideOnParentScroll,
    maxWidth,
    fullBleed,
    inverted,
    message,
    shiftArrow,
    shiftX,
    shiftY,
    alignEdge,
    children,
  } = props;

  /** Get tooltip position from props */
  const position: Position = getPosition(props);

  const [isVisible, setIsVisible] = React.useState<boolean>(false);
  const [target, setTarget] = React.useState<?HTMLElement>(null);

  const tooltipRef = React.useRef<?HTMLElement>(null);
  const mousePositionRef = React.useRef<MousePosition>({
    mouseX: 0,
    mouseY: 0,
  });

  const matchesTarget = React.useCallback(
    /* eslint-disable-next-line no-restricted-globals */
    (x: number, y: number) => target && target.contains(document.elementFromPoint(x, y)),
    [target],
  );

  const matchesTargetEvent = React.useCallback(
    (event: MouseEvent) => matchesTarget(event.clientX, event.clientY),
    [matchesTarget],
  );

  const hide = React.useCallback(() => {
    setIsVisible(false);
  }, []);

  const hideOnScroll = React.useCallback(() => {
    if (!isVisible) {
      return;
    }
    // The only way to get mouse position from a scroll event seems to be to track the location on mouse movements
    // and offset it by the scrolled position
    const { mouseX, mouseY } = mousePositionRef.current;
    /* eslint-disable-next-line no-restricted-globals */
    if (!matchesTarget(window.scrollX + mouseX, window.scrollY + mouseY)) {
      hide();
    }
  }, [matchesTarget, hide, isVisible]);

  const show = React.useCallback(() => {
    if (onShow && !isVisible) {
      onShow();
    }
    setIsVisible(true);
  }, [onShow, isVisible]);

  const toggle = React.useCallback(() => {
    if (isVisible) {
      hide();
    } else {
      show();
    }
  }, [isVisible, hide, show]);

  const showOrHide = React.useCallback(
    throttle((event: MouseEvent) => {
      if (!target || !target.getBoundingClientRect) {
        return false;
      }

      // The cursor is over the target. This test is needed in case the source is bigger than elmt2,
      // because insidePoly() assumes the opposite.
      const matches = matchesTargetEvent(event);
      if (matches) {
        if (!isVisible) {
          show();
        }
        return true;
      }

      if (!tooltipRef.current || !tooltipRef.current.getBoundingClientRect) {
        // Tooltip already closed, or we've clicked and don't want to close
        return false;
      }

      const poly = makePoly(target, tooltipRef.current);
      const isInside = insidePoly([event.clientX, event.clientY], poly);
      if (!isInside) {
        hide();
      }

      return false;
    }, 250),
    [target, tooltipRef, hide, show],
  );

  /** Register the mouse move event listener  */
  useWindow('mousemove', showOrHide, undefined);
  //  Close tooltip on scroll
  useWindowScroll(hideOnScroll);

  const attach = React.useCallback(
    (oldTarget: ?HTMLElement, newTarget: ?HTMLElement, changeTarget: (?HTMLElement) => void) => {
      /**
       *  Callback of mouse move event.
       *  We save the mouse coordinates and later we are using it for detecting if we should display tooltip or not
       */
      const trackMovements = (event: MouseEvent) => {
        /* eslint-disable-next-line no-restricted-globals */
        const mouseX = event.clientX - window.scrollX;
        /* eslint-disable-next-line no-restricted-globals */
        const mouseY = event.clientY - window.scrollY;
        mousePositionRef.current = { mouseX, mouseY };
      };

      /**
       * Everytime when the tooltip  target is changing, we remove all event listeners from old target
       * and register new ones to the new target
       */
      const processEventListeners = (element: HTMLElement, action: EventListenerAction): void => {
        const handleEvent = (type: string, listener: any) =>
          action === 'add'
            ? element.addEventListener(type, listener)
            : element.removeEventListener(type, listener);

        handleEvent('mouseenter', show);
        handleEvent('focus', show);
        handleEvent('blur', hide);
        handleEvent('mousemove', trackMovements);

        if (toggleOnTouch) {
          handleEvent('touchstart', toggle);
        } else {
          handleEvent('touchstart', show);
          handleEvent('touchend', hide);
        }
      };

      if (oldTarget) {
        processEventListeners(oldTarget, 'remove');
      }
      if (newTarget) {
        processEventListeners(newTarget, 'add');
      }
      changeTarget(newTarget);
    },
    [hide, show, toggle, toggleOnTouch],
  );

  /** Custom style for tooltip based on props */
  const customizedStyle = maxWidth ? { maxWidth: `${maxWidth}px` } : {};

  const tooltip = (
    <React.Fragment>
      {children(ref => attach(target, ref, setTarget))}
      {target && isVisible && (
        <AttachedArrowOverlay
          position={position}
          target={target}
          alignEdge={alignEdge}
          small
          inverted={inverted}
          shiftX={shiftX}
          shiftY={shiftY}
          shiftArrow={shiftArrow}
          overlayType="tooltip"
        >
          <div
            className={classnames(styles.tooltip, {
              [styles.inverted]: inverted,
              [styles.fullbleed]: fullBleed,
            })}
            ref={tooltipRef}
            style={customizedStyle}
          >
            {message}
          </div>
        </AttachedArrowOverlay>
      )}
    </React.Fragment>
  );

  if (hideOnParentScroll) {
    return (
      <ParentScroll callback={hide} disableListeners={!isVisible}>
        {r => <span ref={r}> {tooltip}</span>}
      </ParentScroll>
    );
  }

  return tooltip;
};

export default Tooltip;
